import { useEffect, useState } from 'react';

import { courses } from '../../services/api';

interface ResolvedCourseName {
  flowName: string;
  courseId: number | null;
  name: string | null;
  resolvedId: number | null;
  chapterTitle: string | null;
  lessonPosition: number | null;
  lessonCount: number | null;
}

export const useFlowCourseContext = (
  flowName?: string,
  courseId: number | null = null,
): ResolvedCourseName | null => {
  const [resolvedCourse, setResolvedCourse] = useState<ResolvedCourseName | null>(null);

  useEffect(() => {
    let isActive = true;
    setResolvedCourse(null);

    if (!flowName?.trim()) {
      return;
    }

    const loadCourseName = async () => {
      try {
        const response = await courses.getFlowByName(flowName, { courseId });
        const rawName = response?.data?.course?.name;
        const name = typeof rawName === 'string' ? rawName.trim() || null : null;

        if (isActive) {
          setResolvedCourse({ flowName, courseId, name, resolvedId: response?.data?.course?.id ?? courseId, chapterTitle: response?.data?.chapter?.title ?? null, lessonPosition: response?.data?.position_in_course ?? null, lessonCount: response?.data?.course?.total_flows ?? null });
        }
      } catch {
        // Course context is optional presentation data; the flow remains usable.
      }
    };

    void loadCourseName();

    return () => {
      isActive = false;
    };
  }, [courseId, flowName]);

  // A route can render before its effect runs; never show the previous course.
  return resolvedCourse?.flowName === flowName && resolvedCourse?.courseId === courseId
    ? resolvedCourse
    : null;
};

export const useFlowCourseName = (flowName?: string, courseId: number | null = null): string | null =>
  useFlowCourseContext(flowName, courseId)?.name ?? null;
